import { DATA_SETS_ENUM_TYPES } from 'constants/dataSets';
import {
  ADDRESS_BUILDING,
  ADDRESS_CITY,
  ADDRESS_COORDINATES,
  ADDRESS_COUNTRY,
  ADDRESS_POST_CODE,
  ADDRESS_STATE,
  ADDRESS_STREET,
  DATE_CLOSED,
  DATE_OPENED,
  END_DATE,
  EQUITY_SHAREHOLDING,
  ESTIMATED,
  EU_TAXONOMY_CODE,
  FINANCIAL_CONTROL,
  FINISHED_GOOD_STOCK_AMOUNT,
  FINISHED_GOOD_STOCK_AMOUNT_CURRENCY,
  GROSS_MARGIN_ROLLING_AMOUNT,
  GROSS_MARGIN_ROLLING_CURRENCY,
  NACE_CODE,
  NACE_SECTION,
  NET_BOOK,
  NET_BOOK_CURRENCY,
  NUMBER_OF_EMPLOYEES,
  ORG_PATH,
  OWNERSHIP,
  PROFIT_CENTRE_NAME,
  PROFIT_CENTRE_UNIQUE_IDENTIFIER,
  REVENUE_ROLLING_AMOUNT,
  REVENUE_ROLLING_CURRENCY,
  START_DATE,
} from 'constants/formFields';
import generateMockEntityIdentity from 'utils/generateMockEntityIdentity';

import {
  getBalanceSheetAssetsModalMockValues,
  getCustomerModalMockValues,
  getProductModalMockValues,
  getSupplierModalMockValues,
} from './companyMapModalMocks';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type IOrgStructureItem from 'store/slices/dataSets/interfaces/IOrgStructureItem';
import type {
  TCreateCustomerSchema,
  TCreateOperationsSchema,
  TCreateProductSchema,
  TCreateProfitCentreSchema,
  TCreateSupplierSchema,
} from 'utils/validators';

type TOptionGroups = Partial<Record<string, IOption[]>>;

interface IBaseMockOptions {
  enumData?: TOptionGroups,
  naceCodesData?: IOption[],
  countryOptions?: IOption[],
}

interface ICustomerPageMockOptions extends IBaseMockOptions {
  profitCenterOptions?: IOption[],
}

interface ISupplierPageMockOptions extends IBaseMockOptions {
  profitCenterOptions?: IOption[],
}

interface IProductPageMockOptions {
  enumData?: TOptionGroups,
  profitCenterOptions?: IOption[],
  unitLinkOptions?: IOption[],
  relationType?: string | null,
  productCategoriesOptions?: IOption[],
}

interface IOwnOperationsPageMockOptions extends IBaseMockOptions {
  profitCenterOptions?: IOption[],
  companyCurrency?: string,
}

interface IProfitCentrePageMockOptions extends IBaseMockOptions {
  companyCurrency?: string,
  orgStructureData?: IOrgStructureItem[],
}

const FALLBACK_COUNTRY_OPTION: IOption = {
  value: 'US',
  label: 'United States',
};

const FALLBACK_NACE_SECTION: IOption = {
  value: 'A',
  label: 'A - Agriculture, forestry and fishing',
  divisions: [
    {
      value: '01',
      label: '01 - Crop and animal production, hunting and related service activities',
    },
  ],
};

const getNormalizedOptionText = (value?: string | number | null) => String(value ?? '').toLowerCase();

const getPreferredOption = (options: IOption[] = [], preferredValues: string[] = []) => {
  if (!options.length) {
    return undefined;
  }

  const normalizedPreferredValues = preferredValues.map(getNormalizedOptionText);

  if (!normalizedPreferredValues.length) {
    return options[0];
  }

  return options.find((option) => (
    normalizedPreferredValues.includes(getNormalizedOptionText(option.value))
    || normalizedPreferredValues.includes(getNormalizedOptionText(option.label))
  )) || options[0];
};

const getCountryOption = (countryOptions?: IOption[]) => (
  getPreferredOption(countryOptions, [FALLBACK_COUNTRY_OPTION.value, FALLBACK_COUNTRY_OPTION.label || ''])
  || countryOptions?.[0]
  || FALLBACK_COUNTRY_OPTION
);

const getNaceOptions = (naceCodesData?: IOption[]) => {
  const section = naceCodesData?.[0] || FALLBACK_NACE_SECTION;
  const code = section.divisions?.[0] || FALLBACK_NACE_SECTION.divisions?.[0];

  return {
    section,
    code,
  };
};

const getFirstAvailableOrgPath = (items: IOrgStructureItem[] = []): string => items.reduce((currentPath, item) => {
  if (currentPath) {
    return currentPath;
  }

  if (!item.archived) {
    return item.orgPath || item.name;
  }

  return getFirstAvailableOrgPath(item.children);
}, '');

export const getCreateCustomerPageMockValues = ({
  enumData,
  naceCodesData,
  profitCenterOptions,
  countryOptions,
}: ICustomerPageMockOptions): Partial<TCreateCustomerSchema> => (
  getCustomerModalMockValues({
    enumData,
    naceCodesData,
    profitCenterOptions,
    countryOptions,
  })
);

export const getCreateSupplierPageMockValues = ({
  enumData,
  naceCodesData,
  profitCenterOptions,
  countryOptions,
}: ISupplierPageMockOptions): Partial<TCreateSupplierSchema> => {
  const supplierMockValues = getSupplierModalMockValues({
    enumData,
    naceCodesData,
    profitCenterOptions,
    countryOptions,
  });
  const supplierPageMockValues = Object.fromEntries(
    Object.entries(supplierMockValues).filter(([fieldName]) => fieldName !== ESTIMATED),
  );

  return supplierPageMockValues as Partial<TCreateSupplierSchema>;
};

export const getCreateProductPageMockValues = ({
  enumData,
  profitCenterOptions,
  unitLinkOptions,
  relationType,
  productCategoriesOptions,
}: IProductPageMockOptions): Partial<TCreateProductSchema> => (
  getProductModalMockValues({
    enumData,
    profitCenterOptions,
    unitLinkOptions,
    relationType,
    productCategoriesOptions,
  })
);

export const getCreateOwnOperationsPageMockValues = ({
  enumData,
  naceCodesData,
  profitCenterOptions,
  companyCurrency,
  countryOptions,
}: IOwnOperationsPageMockOptions): Partial<TCreateOperationsSchema> => (
  getBalanceSheetAssetsModalMockValues({
    enumData,
    naceCodesData,
    profitCenterOptions,
    companyCurrency,
    countryOptions,
  })
);

export const getCreateProfitCentrePageMockValues = ({
  enumData,
  naceCodesData,
  countryOptions,
  companyCurrency,
  orgStructureData,
}: IProfitCentrePageMockOptions): Partial<TCreateProfitCentreSchema> => {
  const { section, code } = getNaceOptions(naceCodesData);
  const country = getCountryOption(countryOptions);
  const identity = generateMockEntityIdentity({
    baseName: 'Northern Logistics Hub',
    codePrefix: 'PC',
  });
  const parentOrgPath = getFirstAvailableOrgPath(orgStructureData);

  return {
    [PROFIT_CENTRE_NAME]: identity.name,
    [PROFIT_CENTRE_UNIQUE_IDENTIFIER]: identity.uniqueIdentifier,
    [ORG_PATH]: parentOrgPath ? `${parentOrgPath} / ${identity.name}` : identity.name,
    [OWNERSHIP]: getPreferredOption(enumData?.[DATA_SETS_ENUM_TYPES.ownership]),
    [EQUITY_SHAREHOLDING]: '100',
    [FINANCIAL_CONTROL]: '100',
    [NUMBER_OF_EMPLOYEES]: '210',
    [DATE_OPENED]: '2021-03-15',
    [DATE_CLOSED]: '2034-12-31',
    [NACE_SECTION]: section,
    [NACE_CODE]: code,
    [EU_TAXONOMY_CODE]: '6.2',
    [START_DATE]: '2024-01-01',
    [END_DATE]: '2024-12-31',
    [NET_BOOK]: '24.5',
    [NET_BOOK_CURRENCY]: companyCurrency || 'USD',
    [REVENUE_ROLLING_AMOUNT]: '42.8',
    [REVENUE_ROLLING_CURRENCY]: companyCurrency || 'USD',
    [GROSS_MARGIN_ROLLING_AMOUNT]: '11.6',
    [GROSS_MARGIN_ROLLING_CURRENCY]: companyCurrency || 'USD',
    [FINISHED_GOOD_STOCK_AMOUNT]: '7.9',
    [FINISHED_GOOD_STOCK_AMOUNT_CURRENCY]: companyCurrency || 'USD',
    [ADDRESS_COUNTRY]: country,
    [ADDRESS_STATE]: { value: 'North Carolina', label: 'North Carolina' },
    [ADDRESS_POST_CODE]: '28202',
    [ADDRESS_CITY]: { value: 'Charlotte', label: 'Charlotte' },
    [ADDRESS_STREET]: 'Trade Street',
    [ADDRESS_BUILDING]: '101',
    [ADDRESS_COORDINATES]: '35.2271, -80.8431',
  };
};
